import type { ElementRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  signal,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslatePipe } from '@core/i18n-legacy';
import type { ImageMenuCallbacks, ImageMenuData, ImageMenuResult } from '@core/prosemirror-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormFieldComponent,
  IconComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

enum Mode {
  Create = 'create',
  Edit = 'edit',
}

type ImageDialogData = {
  initData: Partial<ImageMenuData>;
  callbacks: ImageMenuCallbacks;
};

@Component({
  selector: 'rt-image-dialog',
  templateUrl: './image-dialog.component.html',
  styleUrl: './image-dialog.component.less',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    CheckboxComponent,
    DialogFooterComponent,
    IconComponent,
    ButtonComponent,
    IconHelpComponent,
    RtTooltip,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {},
})
export class ImageDialogComponent implements DialogComponentI<ImageDialogData, void> {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);

  public mode: Mode;
  public close: () => void;

  public callbacks: ImageMenuCallbacks;
  public form = this.fb.group({
    src: this.fb.control('', [Validators.required]),
    alt: this.fb.control(''),
    caption: this.fb.control(''),
    href: this.fb.control(''),
    targetBlank: this.fb.control(false),
  });

  public wrapperEl = viewChild.required<ElementRef<HTMLElement>>('wrapper');
  public dropAreaEl = viewChild.required<ElementRef<HTMLElement>>('dropArea');
  public isDragoverDialog = signal(false);
  public isDragoverArea = signal(false);

  private file: File | undefined = undefined;

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.cdRef.detectChanges());
  }

  public setDialogData({ initData, callbacks }: ImageDialogData) {
    this.form.patchValue({ ...initData });
    this.callbacks = callbacks;
    this.mode = initData.src ? Mode.Edit : Mode.Create;
  }

  public apply() {
    this.callbacks.save({ ...(<ImageMenuResult>this.form.value), file: this.file });
  }

  public onDrop($event: DragEvent) {
    $event.preventDefault();
    const imageFiles = Array.from($event.dataTransfer?.files || []).filter(file =>
      file.type.includes('image'),
    );
    if (imageFiles.length) {
      this.form.patchValue({ src: URL.createObjectURL(imageFiles[0]) });
      this.file = imageFiles[0];
    }
    this.isDragoverDialog.set(false);
    this.isDragoverArea.set(false);
  }

  public fileInputChanged(files: FileList | null, fileInput: any) {
    const imageFiles = Array.from(files || []).filter(file => file.type.includes('image'));
    if (imageFiles.length) {
      this.form.patchValue({ src: URL.createObjectURL(imageFiles[0]) });
      this.file = imageFiles[0];
    }

    fileInput.value = null;
  }

  public onDragleaveDialog($event: DragEvent) {
    const relatedTarget = <Node>$event.relatedTarget;
    const hostEl = this.wrapperEl().nativeElement;
    if (!hostEl.contains(relatedTarget)) {
      this.isDragoverDialog.set(false);
    }
  }

  public onDragleaveArea($event: DragEvent) {
    const relatedTarget = <Node>$event.relatedTarget;
    const area = this.dropAreaEl().nativeElement;
    if (!area.contains(relatedTarget)) {
      this.isDragoverArea.set(false);
    }
  }
}
